/**
 * 結果のお知らせ（docs/06 4-6）。
 *
 * 操作のあとに「できた／できなかった」を帯で出す部品です（docs/06 原則1・2）。
 * 黙って終わらせないこと、失敗のときは **次に何をすればよいか** まで書くことが要点です。
 *
 * 読み上げにも届くよう、できなかったときは `role="alert"`、それ以外は `role="status"`。
 *
 * 画面の下のボタンを押した結果を画面の上の帯で知らせるときは `reveal` を付けます。
 * 帯が画面の外にあれば、帯が見えるところまで画面を戻します（押したあと「できたのかどうか」が
 * 画面から分からない、を防ぐ・決定22・issue #275 の1）。帯がもう見えていれば動かしません。
 * 画面を開いたときから出ている案内には付けません（開いた途端に画面が動かないように）。
 */
import { useEffect, useRef, type ReactNode } from 'react';

export type ResultTone = 'success' | 'error' | 'info';

/** しるし（色だけで意味を伝えないための記号・docs/06 1-3）。 */
const MARK: Record<ResultTone, string> = {
  success: '✓',
  error: '!',
  info: 'i',
};

export interface ResultBannerProps {
  tone: ResultTone;
  /** 何が起きたか（「3件を無効化しました」「送れませんでした」） */
  message: ReactNode;
  /** 次に何をすればよいか（できなかったときは必ず書く） */
  nextStep?: ReactNode;
  /** その場で押せる次の一手（「もう一度読み込む」など） */
  action?: ReactNode;
  /** 出たとき（言葉が変わったとき）に、帯が見えるところまで画面を戻す */
  reveal?: boolean;
}

export function ResultBanner({
  tone,
  message,
  nextStep,
  action,
  reveal = false,
}: ResultBannerProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!reveal) return;
    // 試験の環境など、画面を動かす仕組みが無いところでは何もしない
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [reveal, tone, message]);
  return (
    <div
      ref={ref}
      className={`ui-banner ui-banner-${tone}`}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
    >
      <span className="ui-banner-mark" aria-hidden="true">
        {MARK[tone]}
      </span>
      <div className="ui-banner-body">
        <p className="ui-banner-message">{message}</p>
        {nextStep !== undefined ? <p className="ui-banner-next">{nextStep}</p> : null}
      </div>
      {action !== undefined ? <div className="ui-banner-action">{action}</div> : null}
    </div>
  );
}
